import Link from "next/link"

import { TOOLS_MANIFEST } from "@/data/tools-manifest"
import { cn } from "@/lib/utils"

type ToolPageLoadingFallbackProps = {
  toolId: string
  className?: string
  headingOverride?: string
}

const FALLBACK_FIELDS: Record<string, string[]> = {
  "aeration-timing-tool": ["ZIP code", "Grass type", "Traffic level"],
  "artificial-turf-cost-calculator": ["Turf area", "ZIP code", "Turf grade"],
  "fertilizer-calculator": ["Lawn size", "Grass type", "NPK rate"],
  "grass-comparison": ["Grass options", "Climate fit", "Maintenance level"],
  "herbicide-timing": ["ZIP code", "Grass type", "Weed pressure"],
  "lawn-cost-estimator": ["Lawn size", "Grass type", "Service level"],
  "lawn-size-calculator": ["Address or ZIP", "Measurement map", "Total square feet"],
  "mowing-calculator": ["Grass type", "Season", "Cut height"],
  "seeding-calculator": ["ZIP code", "Grass type", "Seeding window"],
  "soil-temperature": ["ZIP code", "Current soil temperature", "Planting window"],
  "soil-test-analyzer": ["Soil test PDF", "Grass type", "Lawn size"],
  "watering-schedule": ["ZIP code", "Grass type", "Irrigation zones"],
}

const DEFAULT_FIELDS = ["Location", "Grass type", "Lawn details"]

export function ToolPageLoadingFallback({ toolId, className, headingOverride }: ToolPageLoadingFallbackProps) {
  const tool = TOOLS_MANIFEST.find((entry) => entry.id === toolId)
  const Icon = tool?.icon
  const fields = FALLBACK_FIELDS[toolId] ?? DEFAULT_FIELDS
  const toolName = tool?.name ?? "Lawn Care Tool"
  // This fallback is the tool's crawlable H1 (the island renders post-hydration),
  // so the heading must be able to match the route's <title> query language.
  const heading = headingOverride ?? toolName
  const description = tool?.description ?? "Personalized lawn care recommendations based on your yard details."

  return (
    <div
      className={cn(
        "min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50",
        className,
      )}
      aria-busy="true"
    >
      <div className="mx-auto max-w-5xl px-4 pb-10 pt-4 md:px-6 md:pb-14 md:pt-8 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-7 text-sm text-gray-600">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link href="/" className="hover:text-green-700">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-400">
              /
            </li>
            <li>
              <Link href="/tools" className="hover:text-green-700">
                Tools
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-400">
              /
            </li>
            <li className="font-medium text-gray-900">{toolName}</li>
          </ol>
        </nav>

        <section className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div>
            <div className="mb-4 flex items-center gap-3">
              {Icon && (
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-green-100 bg-white text-green-700 shadow-xs">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
              )}
              <span className="text-sm font-semibold uppercase text-green-700">
                {tool?.category ?? "Lawn care"} tool
              </span>
            </div>
            <h1 className="max-w-3xl text-4xl font-extrabold leading-tight text-gray-950 md:text-5xl">
              {heading}
            </h1>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-gray-700">
              {description}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-base font-semibold text-gray-950">Tool inputs</h2>
              <span className="text-sm text-gray-500">{tool?.estimatedTime ?? "2 minutes"}</span>
            </div>
            <div className="space-y-4">
              {fields.map((field) => (
                <div key={field} className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">{field}</div>
                  <div className="h-11 rounded-lg border border-gray-200 bg-gray-50" />
                </div>
              ))}
              <div className="h-11 rounded-lg bg-green-600/80" />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
